'use client'

import { cn } from '@/lib/utils'

interface MusicEmbedBlockContent {
  platform: 'spotify' | 'apple'
  embedUrl: string
  title?: string
}

interface MusicEmbedBlockProps {
  content: MusicEmbedBlockContent
  className?: string
}

// Extract Spotify embed URL from various formats
function normalizeSpotifyUrl(url: string): string | null {
  try {
    const urlObj = new URL(url)
    
    // Already an embed URL
    if (urlObj.hostname === 'open.spotify.com' && urlObj.pathname.startsWith('/embed/')) {
      return url
    }
    
    // Convert regular Spotify URL to embed URL
    if (urlObj.hostname === 'open.spotify.com') {
      // /track/ID, /album/ID, /playlist/ID, /artist/ID
      const match = urlObj.pathname.match(/^\/(track|album|playlist|artist)\/([^?]+)/)
      if (match) {
        return `https://open.spotify.com/embed/${match[1]}/${match[2]}?utm_source=generator&theme=0`
      }
    }
    
    return null
  } catch {
    return null
  }
}

// Extract Apple Music embed URL from various formats
function normalizeAppleMusicUrl(url: string): string | null {
  try {
    const urlObj = new URL(url)
    
    // Already an embed URL
    if (urlObj.hostname === 'embed.music.apple.com') {
      return url
    }
    
    // Convert regular Apple Music URL to embed URL
    if (urlObj.hostname === 'music.apple.com') {
      // /us/album/album-name/123456789
      // /us/playlist/playlist-name/pl.123456789
      return `https://embed.music.apple.com${urlObj.pathname}${urlObj.search}`
    }
    
    return null
  } catch {
    return null
  }
}

export function MusicEmbedBlock({ content, className }: MusicEmbedBlockProps) {
  if (!content.embedUrl) return null

  const embedUrl = content.platform === 'spotify'
    ? normalizeSpotifyUrl(content.embedUrl)
    : normalizeAppleMusicUrl(content.embedUrl)

  if (!embedUrl) {
    return (
      <div className={cn('p-4 rounded-lg bg-zinc-800/50 border border-zinc-700', className)}>
        <p className="text-zinc-400 text-sm">Invalid {content.platform === 'spotify' ? 'Spotify' : 'Apple Music'} URL</p>
      </div>
    )
  }

  if (content.platform === 'spotify') {
    return (
      <div className={cn('space-y-2', className)}>
        {content.title && (
          <h3 className="text-lg font-semibold text-white">{content.title}</h3>
        )}
        <iframe
          src={embedUrl}
          width="100%"
          height="352"
          frameBorder="0"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          className="rounded-xl"
          title={content.title ? `Spotify: ${content.title}` : 'Spotify music player'}
        />
      </div>
    )
  }

  // Apple Music
  return (
    <div className={cn('space-y-2', className)}>
      {content.title && (
        <h3 className="text-lg font-semibold text-white">{content.title}</h3>
      )}
      <iframe
        allow="autoplay *; encrypted-media *; fullscreen *; clipboard-write"
        frameBorder="0"
        height="450"
        sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
        src={embedUrl}
        className="w-full rounded-xl bg-transparent overflow-hidden"
        title={content.title ? `Apple Music: ${content.title}` : 'Apple Music player'}
      />
    </div>
  )
}



